/** Khối mở rộng ngôn ngữ — hội thoại, thanh điệu, chữ Hán, số đếm. */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Divider,
  Flex,
  Input,
  List,
  Progress,
  Result,
  Segmented,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import { AudioOutlined, PlayCircleOutlined, SoundOutlined, StopOutlined } from "@ant-design/icons";

import type { LangSpeaking } from "@/api/catalog";
import { ScoreTag } from "@/components/ScoreTag";
import { SpeakingPractice } from "@/components/SpeakingPractice";
import {
  classify,
  decodeAudio,
  feats,
  pitchTrack,
  speakerRef,
  trackToPoints,
} from "@/lib/tonePitch";

import {
  HAN_FONT,
  HAN_SERIF,
  TONE_COLORS,
  TONE_MARKS,
  TONE_NAMES,
  chuOf,
  curvePoints,
  playAudio,
  stopAudio,
  useView,
} from "../shared";
import { AudioButton, PitchChart, WordLine } from "../ui";
import type {
  BlockProps,
  ChuHanBlock,
  HoiThoaiBlock,
  HoiThoaiMode,
  NgheChonThanhBlock,
  NgheSoBlock,
  NoiCumBlock,
  NoiThanhBlock,
  SoDemBlock,
  WordObj,
} from "../types";

const { Paragraph, Text } = Typography;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Đối tượng từ → câu luyện nói của `SpeakingPractice` (chấm Gemini theo ngôn ngữ môn). */
function toSpeaking(w: WordObj): LangSpeaking {
  const py = w.py?.join(" ");
  return {
    prompt_vi: [py, w.nghia].filter(Boolean).join(" — ") || undefined,
    target_ja: w.chu,
    audio: w.audio,
  };
}

// ─── Hội thoại: xem / nói đuổi / nhập vai ────────────────────────────────

const MODE_LABEL: Record<HoiThoaiMode, string> = {
  xem: "Xem",
  noi_duoi: "Nói đuổi",
  nhap_vai: "Nhập vai",
};
const SPEAKER_COLORS = ["#d46b08", "#08979c", "#1d39c4", "#722ed1", "#c41d7f"];

export function HoiThoai({ block, lessonSlug }: BlockProps<HoiThoaiBlock>) {
  const sc = block.canh;
  const modes = block.che_do?.length ? block.che_do : (["xem"] as HoiThoaiMode[]);
  const [mode, setMode] = useState<HoiThoaiMode>(modes[0]);
  const [role, setRole] = useState(sc.nhan_vat[0]?.id ?? "");
  const [playing, setPlaying] = useState(-1);
  const [showText, setShowText] = useState(!block.an_phu_de);
  const token = useRef(0);
  const names = useMemo(() => Object.fromEntries(sc.nhan_vat.map((n) => [n.id, n])), [sc.nhan_vat]);
  const colorOf = (id: string) =>
    SPEAKER_COLORS[
      Math.max(
        0,
        sc.nhan_vat.findIndex((n) => n.id === id),
      ) % SPEAKER_COLORS.length
    ];

  useEffect(
    () => () => {
      token.current++;
      stopAudio();
    },
    [],
  );

  const playAll = async () => {
    const mine = ++token.current;
    for (let i = 0; i < sc.cau.length; i++) {
      if (mine !== token.current) return;
      setPlaying(i);
      await playAudio(sc.cau[i].audio);
      await sleep(mode === "noi_duoi" ? 2200 : 350);
    }
    if (mine === token.current) setPlaying(-1);
  };

  const switchMode = (m: HoiThoaiMode) => {
    token.current++;
    stopAudio();
    setPlaying(-1);
    setMode(m);
  };

  const myLines = mode === "nhap_vai" ? sc.cau.filter((c) => c.vai === role) : [];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Flex gap={8} wrap align="center">
        {modes.length > 1 && (
          <Segmented
            value={mode}
            onChange={(v) => switchMode(v as HoiThoaiMode)}
            options={modes.map((m) => ({ value: m, label: MODE_LABEL[m] ?? m }))}
          />
        )}
        {mode === "nhap_vai" && (
          <Segmented
            value={role}
            onChange={(v) => setRole(String(v))}
            options={sc.nhan_vat.map((n) => ({ value: n.id, label: `Tôi là ${n.ten}` }))}
          />
        )}
        <Button icon={<PlayCircleOutlined />} onClick={() => void playAll()}>
          {mode === "noi_duoi" ? "Phát, chừa chỗ để đọc theo" : "Phát cả cảnh"}
        </Button>
        {block.an_phu_de && (
          <Space size={4}>
            <Switch size="small" checked={showText} onChange={setShowText} />
            <Text>Hiện chữ</Text>
          </Space>
        )}
      </Flex>
      <div>
        {sc.cau.map((c, i) => {
          const mine = mode === "nhap_vai" && c.vai === role;
          const who = names[c.vai];
          return (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "40px minmax(0, 1fr) auto",
                gap: 12,
                alignItems: "start",
                padding: "10px 8px",
                borderRadius: 8,
                borderTop: i ? "1px solid #f0f0f0" : undefined,
                background: playing === i ? "#e6f4ff" : "transparent",
              }}
            >
              <Avatar style={{ background: colorOf(c.vai) }}>
                {(who?.ten ?? c.vai).split(" ").pop()?.[0]}
              </Avatar>
              <div style={{ minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {who?.ten ?? c.vai}
                </Text>
                {!showText ? (
                  <div style={{ color: "#bfbfbf" }}>(nghe trước, chữ đang ẩn)</div>
                ) : mine ? (
                  <div style={{ color: "#8c8c8c" }}>Lượt của bạn: {c.nghia}</div>
                ) : (
                  <>
                    <WordLine w={c} size={22} />
                    {c.nghia && <div style={{ fontSize: 13, color: "#595959" }}>{c.nghia}</div>}
                  </>
                )}
              </div>
              {mine ? <Tag color="blue">Bạn nói</Tag> : <AudioButton src={c.audio} />}
            </div>
          );
        })}
      </div>
      {myLines.length > 0 && (
        <>
          <Divider style={{ margin: "4px 0" }} />
          <Text type="secondary">
            Ghi âm lượt nói của bạn ({names[role]?.ten}) — AI chấm phát âm từng câu.
          </Text>
          <SpeakingPractice lessonSlug={lessonSlug} speaking={myLines.map(toSpeaking)} />
        </>
      )}
    </Space>
  );
}

// ─── Nghe và chọn thanh ──────────────────────────────────────────────────

export function NgheChonThanh({ block }: BlockProps<NgheChonThanhBlock>) {
  const items = block.cau;
  const [i, setI] = useState(0);
  const [ans, setAns] = useState<number | null>(null);
  const [right, setRight] = useState(0);

  if (i >= items.length) {
    const score = (right / items.length) * 10;
    return (
      <Result
        status={score >= 8 ? "success" : "info"}
        title={
          <span>
            Đúng {right}/{items.length} câu · <ScoreTag value={score} />
          </span>
        }
        subTitle="Luyện tập, không tính vào điểm môn."
        extra={
          <Button
            onClick={() => {
              setI(0);
              setAns(null);
              setRight(0);
            }}
          >
            Làm lại
          </Button>
        }
      />
    );
  }
  const it = items[i];
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Flex align="center" gap={12} wrap>
        <Text type="secondary">
          Câu {i + 1}/{items.length}
        </Text>
        <Progress
          percent={Math.round((i / items.length) * 100)}
          showInfo={false}
          style={{ flex: 1, minWidth: 120, margin: 0 }}
        />
        {it.giong && <Tag>{it.giong === "nam" ? "Giọng nam" : "Giọng nữ"}</Tag>}
      </Flex>
      <Flex align="center" gap={12}>
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<SoundOutlined />}
          aria-label="Nghe"
          onClick={() => void playAudio(it.audio)}
        />
        <Text type="secondary">Nghe rồi chọn thanh. Nghe lại bao nhiêu lần cũng được.</Text>
      </Flex>
      <Flex gap={8} wrap>
        {[1, 2, 3, 4].map((t) => (
          <Button
            key={t}
            size="large"
            type={ans !== null && t === it.thanh ? "primary" : "default"}
            danger={ans === t && t !== it.thanh}
            onClick={() => {
              if (ans !== null) return;
              setAns(t);
              if (t === it.thanh) setRight((r) => r + 1);
            }}
          >
            <span style={{ fontSize: 20, fontWeight: 700, marginRight: 6, color: TONE_COLORS[t] }}>
              {TONE_MARKS[t]}
            </span>
            {TONE_NAMES[t]}
          </Button>
        ))}
      </Flex>
      {ans !== null && (
        <Alert
          type={ans === it.thanh ? "success" : "error"}
          showIcon
          message={
            <span>
              <span style={{ fontFamily: HAN_FONT, fontSize: 18 }}>{it.chu}</span>{" "}
              <span style={{ color: TONE_COLORS[it.thanh] }}>{it.py}</span>
              {it.nghia ? ` — ${it.nghia}` : ""}
            </span>
          }
          description={
            ans === it.thanh
              ? "Đúng."
              : `Bạn chọn ${TONE_NAMES[ans]}. Đây là ${TONE_NAMES[it.thanh]}.`
          }
          action={
            <Button
              type="primary"
              onClick={() => {
                setI(i + 1);
                setAns(null);
              }}
            >
              Câu tiếp
            </Button>
          }
        />
      )}
    </Space>
  );
}

// ─── Đọc thanh, chấm ngay trên trình duyệt (audio không rời máy) ──────────

type MicState = "idle" | "recording" | "analyzing";
const MAX_RECORD_MS = 4000;

interface ToneResult {
  got: 1 | 2 | 3 | 4 | null;
  points: [number, number][];
}

export function NoiThanh({ block }: BlockProps<NoiThanhBlock>) {
  const [k, setK] = useState(Math.min(2, block.cau.length - 1));
  const it = block.cau[k];
  const [res, setRes] = useState<ToneResult | null>(null);
  const [mic, setMic] = useState<MicState>("idle");
  const [err, setErr] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    setRes(null);
    setErr(null);
  }, [k]);
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
      recorder.current?.stream.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  const canRecord =
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;

  const analyze = async (blob: Blob) => {
    setMic("analyzing");
    try {
      const { data, sr } = await decodeAudio(await blob.arrayBuffer());
      const track = pitchTrack(data, sr);
      if (track.voicedFrames < 6) {
        setErr("Chưa nghe rõ giọng — đọc to, rõ và gần micro hơn rồi thử lại.");
        setRes(null);
      } else {
        const ref = speakerRef(track.med);
        setRes({ got: classify(feats(track.hz, ref)), points: trackToPoints(track.hz, ref) });
        setErr(null);
      }
    } catch {
      setErr("Không phân tích được bản ghi trên trình duyệt này.");
    } finally {
      setMic("idle");
    }
  };

  const start = async () => {
    setErr(null);
    stopAudio();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        void analyze(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
      };
      recorder.current = rec;
      rec.start();
      setMic("recording");
      timer.current = window.setTimeout(() => {
        if (rec.state === "recording") rec.stop();
      }, MAX_RECORD_MS);
    } catch {
      setErr("Không mở được micro. Kiểm tra quyền micro của trình duyệt rồi thử lại.");
      setMic("idle");
    }
  };

  const stop = () => {
    if (timer.current) window.clearTimeout(timer.current);
    if (recorder.current?.state === "recording") recorder.current.stop();
  };

  const ok = res !== null && res.got === it.thanh;
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Segmented
        value={k}
        onChange={(v) => setK(Number(v))}
        options={block.cau.map((c, j) => ({
          value: j,
          label: <span style={{ fontFamily: HAN_FONT }}>{c.chu}</span>,
        }))}
      />
      <Flex gap={24} wrap align="flex-start">
        <div style={{ minWidth: 120 }}>
          <div style={{ fontFamily: HAN_SERIF, fontSize: 64, lineHeight: 1.1 }}>{it.chu}</div>
          <div style={{ color: TONE_COLORS[it.thanh], fontSize: 20 }}>{it.py}</div>
          <Tag style={{ marginTop: 6 }}>{TONE_NAMES[it.thanh]}</Tag>
        </div>
        <div style={{ flex: "1 1 280px", minWidth: 0 }}>
          <PitchChart
            model={curvePoints(it.curve)}
            user={res?.points ?? null}
            color={TONE_COLORS[it.thanh]}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Vệt màu: giọng mẫu. Nét đen: giọng của bạn, chuẩn hoá theo âm vực riêng.
          </Text>
        </div>
      </Flex>
      <Flex gap={8} wrap>
        <Button icon={<SoundOutlined />} onClick={() => void playAudio(it.audio)}>
          Nghe mẫu
        </Button>
        {mic === "recording" ? (
          <Button danger type="primary" icon={<StopOutlined />} onClick={stop}>
            Dừng
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<AudioOutlined />}
            loading={mic === "analyzing"}
            disabled={!canRecord}
            onClick={() => void start()}
          >
            Ghi âm
          </Button>
        )}
      </Flex>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {canRecord
          ? "Âm thanh chỉ được phân tích ngay trên máy bạn, không gửi lên máy chủ. Luyện tập, không tính điểm."
          : "Trình duyệt này chưa hỗ trợ ghi âm — hãy dùng Chrome, Edge, Firefox hoặc Safari mới."}
      </Text>
      {err && <Alert type="warning" showIcon message={err} />}
      {res && (
        <Alert
          showIcon
          type={ok ? "success" : "warning"}
          message={
            ok
              ? `Đạt · máy nghe ra ${TONE_NAMES[res.got as number]}`
              : `Chưa đạt · máy nghe ra ${res.got ? TONE_NAMES[res.got] : "chưa rõ thanh"}, cần ${TONE_NAMES[it.thanh]}`
          }
          description={
            ok
              ? "Đường của bạn bám đúng dáng thanh mẫu."
              : "So nét đen với vệt màu: nhìn chỗ đi lên, đi xuống khác mẫu rồi đọc lại."
          }
        />
      )}
    </Space>
  );
}

// ─── Đọc theo cụm (chấm máy chủ — SpeakingPractice, Gemini theo ngôn ngữ môn) ─

export function NoiCum({ block, lessonSlug }: BlockProps<NoiCumBlock>) {
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <List
        dataSource={block.cau}
        renderItem={(w) => (
          <List.Item actions={[<AudioButton key="n" src={w.audio} label={`Nghe mẫu ${w.chu}`} />]}>
            <Flex gap={16} align="center" wrap>
              <WordLine w={w} size={22} />
              {w.curve && (
                <div style={{ width: 160 }}>
                  <PitchChart model={curvePoints(w.curve)} color="#1677ff" height={80} />
                </div>
              )}
            </Flex>
          </List.Item>
        )}
      />
      <SpeakingPractice lessonSlug={lessonSlug} speaking={block.cau.map(toSpeaking)} />
    </Space>
  );
}

// ─── Chữ Hán và thành phần ───────────────────────────────────────────────

export function ChuHan({ block }: BlockProps<ChuHanBlock>) {
  const v = useView();
  return (
    <Flex wrap gap={12}>
      {block.chu.map((c) => (
        <Card key={c.chu} size="small" style={{ width: 196 }}>
          <div
            style={{ fontFamily: HAN_SERIF, fontSize: 56, textAlign: "center", lineHeight: 1.1 }}
          >
            {chuOf(c, v.phon)}
          </div>
          <div style={{ textAlign: "center", color: "#8c8c8c", fontStyle: "italic" }}>
            {c.hv}
            {c.phon && c.phon !== c.chu && (
              <>
                {" · "}
                {v.phon ? "giản thể " : "phồn thể "}
                <span style={{ fontFamily: HAN_FONT, fontStyle: "normal" }}>
                  {v.phon ? c.chu : c.phon}
                </span>
              </>
            )}
          </div>
          {!!c.thanh_phan?.length && (
            <>
              <Divider style={{ margin: "8px 0" }} />
              <Space direction="vertical" size={2}>
                {c.thanh_phan.map((t, j) => (
                  <div key={j}>
                    <Tag style={{ fontFamily: HAN_FONT }}>{t.phan}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t.giai_nghia}
                    </Text>
                  </div>
                ))}
              </Space>
            </>
          )}
          {c.meo && (
            <Paragraph type="secondary" style={{ fontSize: 12, margin: "6px 0 0" }}>
              {c.meo}
            </Paragraph>
          )}
        </Card>
      ))}
    </Flex>
  );
}

// ─── Số đếm + nghe gõ số ─────────────────────────────────────────────────

export function SoDem({ block }: BlockProps<SoDemBlock>) {
  const v = useView();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))",
        gap: 8,
      }}
    >
      {block.so.map((s) => (
        <Card
          key={s.so}
          size="small"
          hoverable
          role="button"
          aria-label={`Nghe số ${s.so}`}
          onClick={() => void playAudio(s.audio)}
          style={{ textAlign: "center" }}
        >
          <div style={{ fontSize: 13, color: "#8c8c8c" }}>{s.so}</div>
          <div style={{ fontFamily: HAN_SERIF, fontSize: 34, lineHeight: 1.2 }}>{s.chu}</div>
          {v.py && s.py && <div style={{ color: TONE_COLORS[s.thanh ?? 0] }}>{s.py}</div>}
        </Card>
      ))}
    </div>
  );
}

export function NgheSo({ block }: BlockProps<NgheSoBlock>) {
  const [val, setVal] = useState<Record<number, string>>({});
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {block.cau.map((q, i) => (
        <Flex key={i} gap={8} align="center" wrap>
          <Text strong>{i + 1}.</Text>
          <AudioButton src={q.audio} />
          <Input
            value={val[i] ?? ""}
            onChange={(e) => setVal({ ...val, [i]: e.target.value.replace(/\D/g, "") })}
            placeholder="Gõ dãy số nghe được"
            inputMode="numeric"
            aria-label={`Câu ${i + 1}: dãy số nghe được`}
            style={{ width: 200 }}
          />
          <Button onClick={() => setChecked({ ...checked, [i]: true })}>Kiểm tra</Button>
          {checked[i] &&
            (val[i] === q.dap_an ? (
              <Tag color="success">Đúng</Tag>
            ) : (
              <Tag color="error">Đáp án: {q.dap_an}</Tag>
            ))}
        </Flex>
      ))}
    </Space>
  );
}
